'use client';

import { useState } from 'react';
import Link from 'next/link';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqs: FAQItem[] = [
  // Getting Started
  {
    category: 'Getting Started',
    question: 'What is The Art of CTO?',
    answer: 'The Art of CTO is a comprehensive resource platform for technical leaders. We provide timely insights on current challenges and timeless frameworks for foundational decisions. Our content covers everything from system architecture to team leadership.',
  },
  {
    category: 'Getting Started',
    question: 'Is all content free?',
    answer: 'Yes! All articles, frameworks, comparisons, and code samples are freely accessible. We believe in making high-quality technical leadership content available to everyone. Premium features like personalized dashboards and advanced tools may be introduced in the future.',
  },
  {
    category: 'Getting Started',
    question: 'How often is new content published?',
    answer: 'We publish new insights and frameworks regularly. Subscribe to our RSS feed or newsletter to stay updated on new content. Our AI-powered curation system also surfaces timely topics from across the tech industry.',
  },
  {
    category: 'Getting Started',
    question: 'Can I contribute content?',
    answer: 'Absolutely! We welcome contributions from experienced technical leaders. If you have insights, frameworks, or case studies to share, reach out through our contact form. All contributions are reviewed for quality and relevance.',
  },
  // Account & Access
  {
    category: 'Account & Access',
    question: 'Do I need an account to access content?',
    answer: 'No account is required to read articles and access most features. Creating an account unlocks additional features like bookmarks, personalized recommendations, and the Command Center for advanced analysis tools.',
  },
  {
    category: 'Account & Access',
    question: 'How do I reset my password?',
    answer: 'Click the "Forgot Password" link on the login page and enter your email address. You will receive a password reset link within a few minutes. If you do not receive the email, check your spam folder or contact support.',
  },
  {
    category: 'Account & Access',
    question: 'Can I delete my account?',
    answer: 'Yes, you can request account deletion at any time. Go to Dashboard > Settings and click "Delete Account". This will permanently remove your account and all associated data. This action cannot be undone.',
  },
  // Technical
  {
    category: 'Technical',
    question: 'Which browsers are supported?',
    answer: 'We support all modern browsers including Chrome, Firefox, Safari, and Edge. For the best experience, we recommend using the latest version of your preferred browser. Internet Explorer is not supported.',
  },
  {
    category: 'Technical',
    question: 'Is there a mobile app?',
    answer: 'The Art of CTO is a Progressive Web App (PWA), meaning you can install it on your device directly from your browser. On mobile, tap the share button and select "Add to Home Screen". This provides an app-like experience without needing an app store.',
  },
  {
    category: 'Technical',
    question: 'Can I use the content offline?',
    answer: 'Yes! As a PWA, previously visited pages are cached for offline access. Install the app to your device for the best offline experience. Note that some interactive features require an internet connection.',
  },
  {
    category: 'Technical',
    question: 'Are there API access options?',
    answer: 'We currently do not offer a public API. If you have specific integration needs for your organization, please contact us to discuss enterprise options.',
  },
  // Content & Features
  {
    category: 'Content & Features',
    question: 'What are Insights vs Frameworks?',
    answer: 'Insights are timely perspectives on current challenges, industry trends, and practical advice. Frameworks are timeless decision-making models and processes that remain relevant regardless of technology changes. Both are essential for technical leadership.',
  },
  {
    category: 'Content & Features',
    question: 'How do the comparison tools work?',
    answer: 'Our comparison tools provide side-by-side evaluations of databases, frameworks, and technologies. Each comparison includes objective criteria, use case recommendations, and community insights to help you make informed decisions.',
  },
  {
    category: 'Content & Features',
    question: 'What is the Command Center?',
    answer: 'The Command Center is our advanced analysis hub featuring tools like SplitCause (causal analysis), Tech Stack Dashboard, and more. These tools help you analyze complex technical decisions and communicate effectively with stakeholders.',
  },
  {
    category: 'Content & Features',
    question: 'Can I download templates and code samples?',
    answer: 'Yes! All templates and code samples are available for download. Code is provided under the MIT license unless otherwise noted. Templates can be customized for your organization needs.',
  },
];

const categories = [...new Set(faqs.map(faq => faq.category))];

export default function SupportPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'All' || faq.category === activeCategory;
    const matchesSearch = searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm text-light-text-secondary dark:text-foreground-tertiary">
          <Link href="/" className="hover:text-accent transition-colors">
            Home
          </Link>
          <span className="mx-2">/</span>
          <span className="text-light-text dark:text-foreground">Support</span>
        </nav>

        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-light-text dark:text-foreground mb-4">
            Support Center
          </h1>
          <p className="text-xl text-light-text-secondary dark:text-foreground-secondary max-w-2xl mx-auto">
            Find answers to common questions, get help with your account, or reach out to our team.
          </p>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-3 gap-4 mb-12">
          <Link
            href="#faq"
            className="p-6 bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border-muted hover:border-accent/40 transition-colors text-center"
          >
            <div className="text-3xl mb-3">?</div>
            <h3 className="font-bold text-light-text dark:text-foreground mb-2">FAQ</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary">
              Browse frequently asked questions
            </p>
          </Link>
          <Link
            href="#contact"
            className="p-6 bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border-muted hover:border-accent/40 transition-colors text-center"
          >
            <div className="text-3xl mb-3">@</div>
            <h3 className="font-bold text-light-text dark:text-foreground mb-2">Contact Us</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary">
              Get in touch with our team
            </p>
          </Link>
          <Link
            href="/about"
            className="p-6 bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border-muted hover:border-accent/40 transition-colors text-center"
          >
            <div className="text-3xl mb-3">i</div>
            <h3 className="font-bold text-light-text dark:text-foreground mb-2">About</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary">
              Learn more about our platform
            </p>
          </Link>
        </div>

        {/* FAQ Section */}
        <section id="faq" className="mb-16">
          <h2 className="text-3xl font-bold text-light-text dark:text-foreground mb-6 pb-2 border-b border-light-border dark:border-border">
            Frequently Asked Questions
          </h2>

          {/* Search */}
          <div className="mb-6">
            <input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 bg-light-surface dark:bg-surface border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground placeholder:text-light-text-tertiary dark:placeholder:text-foreground-muted focus:outline-none focus:border-accent"
            />
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setActiveCategory('All')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === 'All'
                  ? 'bg-accent text-accent-foreground'
                  : 'bg-light-surface dark:bg-surface text-light-text dark:text-foreground border border-light-border dark:border-border hover:border-accent/40'
              }`}
            >
              All
            </button>
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === category
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-light-surface dark:bg-surface text-light-text dark:text-foreground border border-light-border dark:border-border hover:border-accent/40'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* FAQ Items */}
          <div className="space-y-3">
            {filteredFAQs.length === 0 ? (
              <div className="text-center py-8 text-light-text-secondary dark:text-foreground-tertiary">
                No questions found matching your search.
              </div>
            ) : (
              filteredFAQs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border-muted overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-light-surface/80 dark:hover:bg-surface/80 transition-colors"
                  >
                    <span className="font-medium text-light-text dark:text-foreground pr-4">
                      {faq.question}
                    </span>
                    <span className="text-accent text-xl flex-shrink-0">
                      {openFAQ === index ? '−' : '+'}
                    </span>
                  </button>
                  {openFAQ === index && (
                    <div className="px-6 pb-4 text-light-text-secondary dark:text-foreground-secondary">
                      <div className="text-xs text-accent mb-2">{faq.category}</div>
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </section>

        {/* Documentation Links */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-light-text dark:text-foreground mb-6 pb-2 border-b border-light-border dark:border-border">
            Documentation & Resources
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              href="/insights"
              className="p-6 bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border-muted hover:border-accent/40 transition-colors"
            >
              <h3 className="font-bold text-light-text dark:text-foreground mb-2">Insights</h3>
              <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary">
                Timely perspectives on leadership and technical decisions
              </p>
            </Link>
            <Link
              href="/frameworks"
              className="p-6 bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border-muted hover:border-accent/40 transition-colors"
            >
              <h3 className="font-bold text-light-text dark:text-foreground mb-2">Frameworks</h3>
              <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary">
                Decision-making frameworks for technical leaders
              </p>
            </Link>
            <Link
              href="/comparisons"
              className="p-6 bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border-muted hover:border-accent/40 transition-colors"
            >
              <h3 className="font-bold text-light-text dark:text-foreground mb-2">Comparisons</h3>
              <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary">
                Side-by-side technology evaluations
              </p>
            </Link>
            <Link
              href="/code"
              className="p-6 bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border-muted hover:border-accent/40 transition-colors"
            >
              <h3 className="font-bold text-light-text dark:text-foreground mb-2">Code Samples</h3>
              <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary">
                Production-ready code patterns and implementations
              </p>
            </Link>
            <Link
              href="/templates"
              className="p-6 bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border-muted hover:border-accent/40 transition-colors"
            >
              <h3 className="font-bold text-light-text dark:text-foreground mb-2">Templates</h3>
              <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary">
                Ready-to-use documents and templates
              </p>
            </Link>
            <Link
              href="/questions"
              className="p-6 bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border-muted hover:border-accent/40 transition-colors"
            >
              <h3 className="font-bold text-light-text dark:text-foreground mb-2">Interview Questions</h3>
              <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary">
                Curated questions for technical hiring
              </p>
            </Link>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="mb-16">
          <h2 className="text-3xl font-bold text-light-text dark:text-foreground mb-6 pb-2 border-b border-light-border dark:border-border">
            Contact Us
          </h2>
          <div className="bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border-muted p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-bold text-light-text dark:text-foreground mb-4">Get in Touch</h3>
                <p className="text-light-text-secondary dark:text-foreground-secondary mb-6">
                  Have a question that is not answered here? Want to contribute content or report an issue?
                  We would love to hear from you.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="text-accent">@</span>
                    <div>
                      <div className="font-medium text-light-text dark:text-foreground">Email</div>
                      <a
                        href="mailto:support@theartofcto.com"
                        className="text-accent hover:underline"
                      >
                        support@theartofcto.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-accent">#</span>
                    <div>
                      <div className="font-medium text-light-text dark:text-foreground">GitHub</div>
                      <a
                        href="https://github.com/theartofcto"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent hover:underline"
                      >
                        Report an issue
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-bold text-light-text dark:text-foreground mb-4">Response Times</h3>
                <div className="space-y-3 text-light-text-secondary dark:text-foreground-secondary">
                  <div className="flex justify-between">
                    <span>General inquiries</span>
                    <span className="text-light-text dark:text-foreground">1-2 business days</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Technical issues</span>
                    <span className="text-light-text dark:text-foreground">24-48 hours</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Content contributions</span>
                    <span className="text-light-text dark:text-foreground">3-5 business days</span>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-accent-muted rounded-lg">
                  <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                    <span className="font-medium text-light-text dark:text-foreground">Pro tip:</span> Check the FAQ above first - many common questions are already answered there.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Status & Updates */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-light-text dark:text-foreground mb-6 pb-2 border-b border-light-border dark:border-border">
            Status & Updates
          </h2>
          <div className="bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border-muted p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="font-medium text-light-text dark:text-foreground">All Systems Operational</span>
            </div>
            <p className="text-light-text-secondary dark:text-foreground-secondary mb-4">
              Last checked: Just now
            </p>
            <div className="flex gap-4">
              <Link
                href="/feed.xml"
                className="text-sm text-accent hover:underline"
              >
                RSS Feed
              </Link>
              <Link
                href="/newsletter"
                className="text-sm text-accent hover:underline"
              >
                Newsletter
              </Link>
            </div>
          </div>
        </section>

        {/* Community */}
        <section>
          <div className="bg-gradient-to-r from-accent-muted to-accent/5 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-4">
              Join the Community
            </h2>
            <p className="text-light-text-secondary dark:text-foreground-secondary mb-6 max-w-xl mx-auto">
              Connect with other technical leaders. Share experiences, ask questions, and learn from peers navigating similar challenges.
            </p>
            <div className="flex justify-center gap-4">
              <Link
                href="/newsletter"
                className="px-6 py-3 bg-accent text-accent-foreground font-bold rounded-lg hover:bg-accent-hover transition-colors"
              >
                Subscribe to Newsletter
              </Link>
              <Link
                href="/about"
                className="px-6 py-3 bg-light-surface dark:bg-surface text-light-text dark:text-foreground border border-light-border dark:border-border font-bold rounded-lg hover:border-accent/40 transition-colors"
              >
                Learn More
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
